import React from 'react';
import { Formik, Form } from 'formik';
import { Row, Col, Button } from 'reactstrap';
import validationSchema from './validationSchema';
import { useLocation } from 'react-router-dom';

import {
  formatIsoDate,
  FormikInput as Input,
  SystemOfMeasuresEnum,
  useTranslator,
  useToast,
  callNativeAppRemoveLastRoute,
  callNativeMobileApp,
} from '@front-activerse/common-components';

import { useNavigate } from 'react-router-dom';
import { saveMeasurementsArgType } from '../types';

interface IMeasurementFormProps {
  saveMeasurements: (formData: saveMeasurementsArgType) => {
    goalLostBecauseOfLoseWeight: boolean;
    goalLostBecauseOfPutOnWeight: boolean;
    goalReachedBecauseOfLoseWeight: boolean;
    goalReachedBecauseOfPutOnWeight: boolean;
    code: number;
  };
  systemOfMeasures: SystemOfMeasuresEnum;
  measurements?: {
    weight: string | number;
    hip: string | number;
    waist: string | number;
    thigh: string | number;
    rightthigh?: string | number;
    arm?: string | number;
    rightarm?: string | number;
    chest?: string | number;
  };
  date?: string;
  availableMeasurements: string[][];
}

const MeasurementForm: React.FC<IMeasurementFormProps> = ({
  systemOfMeasures,
  saveMeasurements,
  measurements,
  date,
  availableMeasurements,
}) => {
  const { toastSuccess, toastError } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslator();
  const { state } = useLocation();

  const initialValues = {
    systemOfMeasures,
    weight: measurements?.weight || '',
    hip: measurements?.hip || '',
    waist: measurements?.waist || '',
    thigh: measurements?.thigh || '',
    rightthigh: measurements?.rightthigh || '',
    arm: measurements?.arm || '',
    rightarm: measurements?.rightarm || '',
    chest: measurements?.chest || '',
    date: date || formatIsoDate(new Date()),
  };

  const handleSubmit = async (values: typeof initialValues) => {
    const variables: any = {
      systemOfMeasures,
      date: values.date,
    };

    // send to backend only filled fields
    const bodyParts = ['weight', 'hip', 'waist', 'thigh', 'rightthigh', 'arm', 'rightarm', 'chest'];

    bodyParts.forEach((part) => {
      if (values[part as keyof typeof values]) {
        variables[part] = +values[part as keyof typeof values];
      }
    });

    try {
      const {
        goalLostBecauseOfLoseWeight,
        goalLostBecauseOfPutOnWeight,
        goalReachedBecauseOfLoseWeight,
        goalReachedBecauseOfPutOnWeight,
        code,
      } = await saveMeasurements(variables);
      if (
        goalLostBecauseOfLoseWeight ||
        goalLostBecauseOfPutOnWeight ||
        goalReachedBecauseOfLoseWeight ||
        goalReachedBecauseOfPutOnWeight ||
        code === 200
      ) {
        toastSuccess(
          <p className="mb-0 text-center">
            {t('measurements.addMeasurement.confirmationNotification')}
          </p>
        );

        // action after success
        callNativeMobileApp({
          action: 'refresh_data',
          payload: {
            module: 'measurements',
          },
        });

        if (state?.from?.pathname) {
          // when open in next page go back
          navigate(-1);
          callNativeAppRemoveLastRoute();
        } else {
          // when open in modal close modal
          callNativeMobileApp({
            action: 'close_modal',
          });
        }
      } else {
        toastError();
      }
    } catch (err) {
      toastError();
    }
  };

  return (
    <Formik
      validationSchema={() => validationSchema(t)}
      validateOnChange
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({ values, isValid, isSubmitting }) => {
        const hasInputValue =
          values.hip ||
          values.waist ||
          values.weight ||
          values.thigh ||
          values.rightthigh ||
          values.arm ||
          values.rightarm ||
          values.chest;
        const isDisabled = !isValid || !hasInputValue;

        return (
          <Form>
            <Input name="systemOfMeasures" type="hidden" label="" />
            <Row>
              {availableMeasurements?.map((measurementGroup) => {
                return measurementGroup.map((measurementType, index) => {
                  let unit, step;

                  if (measurementType === 'weight') {
                    unit = systemOfMeasures === 'imperial' ? 'lb' : 'kg';
                    step = systemOfMeasures === 'imperial' ? '1' : '0.1';
                  } else {
                    unit = systemOfMeasures === 'imperial' ? 'in' : 'cm';
                    step = systemOfMeasures === 'imperial' ? '1' : '0.1';
                  }

                  // Determine className based on the position of the measurementType
                  let className = '';
                  if (measurementGroup.length > 1) {
                    if (index === 0) {
                      className = 'pe-1';
                    } else if (index === measurementGroup.length - 1) {
                      className = 'ps-1';
                    } else {
                      className = 'ps-1 pe-1';
                    }
                  }

                  return (
                    <Col
                      xs={12 / measurementGroup.length}
                      key={measurementType}
                      className={className}
                    >
                      <Input
                        name={measurementType}
                        type="number"
                        label={t(`measurements.${measurementType}`)}
                        unit={unit}
                        translateErrMsg={false}
                        step={step}
                      />
                    </Col>
                  );
                });
              })}
            </Row>

            <Input
              name="date"
              type="date"
              label={t('measurements.date')}
              max={formatIsoDate(new Date())}
              disabled={!!date}
            />

            <Row className="mt-xl">
              <Col xs="6" className="pe-1">
                <Button
                  color="secondary"
                  outline
                  className="w-100"
                  onClick={() => {
                    navigate(-1);
                    callNativeAppRemoveLastRoute();
                  }}
                >
                  {t('measurements.cancelButton')}
                </Button>
              </Col>
              <Col xs="6" className="ps-1">
                <Button
                  className="w-100"
                  color="primary"
                  type="submit"
                  disabled={isDisabled || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      <span>{t('measurements.saveButton')}</span>
                    </>
                  ) : (
                    t('measurements.saveButton')
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
};

export default MeasurementForm;
